/* eslint-disable max-lines */
import { zodResolver } from '@hookform/resolvers/zod';
import { AgreementType, InlineAlertType } from '@varo/graphql-schema-types';
import PaymentOTP from 'features/v2a/receiver/components/PaymentOTP/PaymentOTP';
import ReceiverHeader from 'features/v2a/receiver/components/ReceiverHeader/ReceiverHeader';
import { paymentDetailsFormSchema } from 'features/v2a/receiver/helpers/PaymentDetailForm.schema';
import {
  DEFAULT_VALUES,
  ErrorViewType,
  formatExpDate,
  openAgreementLink,
  V2ABankAccountForm,
} from 'features/v2a/receiver/helpers/V2A.helpers';
import usePaymentDetails from 'features/v2a/receiver/hooks/usePaymentDetails';
import { V2AReceiverContext } from 'features/v2a/receiver/providers/V2AReceiverContextProvider';
import { stateList } from 'features/v2a/sender/helpers/Sender.helpers';
import { jwtDecode } from 'jwt-decode';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import {
  KeyboardAwareScrollView,
  KeyboardToolbar,
  KeyboardToolbarProps,
} from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import VXLDropdown from 'shared/components/vxl/input/VXLDropdown/VXLDropdown';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import Checkbox from 'shared/components/vxl/selection/Checkbox/Checkbox';
import ExpressiveSmText from 'shared/components/vxl/typography/expressive/ExpressiveSmText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import { SMALL_SCREEN_MAX_WIDTH } from 'shared/constants/layout';
import { isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import {
  findFullStateString,
  getStateAbbreviation,
  getStateString,
} from 'shared/utils/smartyStreet/smartyStreet';
import { formatCardNumber } from 'shared/utils/string/cardNumber';
import { filterDigits } from 'shared/utils/string/string.helpers';

const PaymentsDetails = () => {
  const [challengeVerified, setChallengeVerified] = useState(false);
  const [formData, setFormData] = useState<V2ABankAccountForm>();

  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'paymentDetails',
  });

  const { paymentDetails } = useContext(V2AReceiverContext);

  const {
    submitForm,
    challengeData,
    errorResponse,
    agreementData,
    submitting,
  } = usePaymentDetails();

  const {
    control,
    getValues,
    trigger,
    formState: { errors, isValid },
    setFocus,
    handleSubmit,
  } = useForm<V2ABankAccountForm>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(paymentDetailsFormSchema),
  });

  const decoded: { rid?: string } = paymentDetails?.token
    ? jwtDecode(paymentDetails.token)
    : {};
  const receiverId = decoded.rid;
  const customAttributes = { receiver_id: receiverId ?? '' };
  const logTermsAndConditionAccessedAnalytics = usePressableAnalytics({
    analyticsId: 'v2aTermsAndConditionAccess',
    customAttributes: customAttributes,
  });

  const handleTermsLinkPress = () => {
    logTermsAndConditionAccessedAnalytics();
    void openAgreementLink(
      AgreementType.VaroToAnyoneUserAgreement,
      agreementData?.agreements,
    );
  };

  const logErrorAnalytics = usePressableAnalytics({
    analyticsId: 'v2aPaymentDetailsErrorEncountered',
    customAttributes: {
      receiver_id: receiverId,
      error_name: errorResponse?.error,
      error_description: errorResponse?.subtitle,
    },
  });

  useEffect(() => {
    if (errorResponse) {
      logErrorAnalytics();
    }
  }, [errorResponse, logErrorAnalytics]);

  const handlePolicyLinkPress = () =>
    void openAgreementLink(
      AgreementType.OnlineAndMobilePrivacyPolicy,
      agreementData?.agreements,
    );

  const formSubmit = useCallback(async () => {
    const formData = getValues();
    setFormData(formData);
    await submitForm(formData);
  }, [getValues, submitForm]);

  const onSubmitAction = async () => {
    await trigger();

    if (isValid && !(Object.entries(errors).length > 0)) {
      void formSubmit();
    }
  };

  useEffect(() => {
    if (challengeVerified) {
      void formSubmit();
    }
  }, [challengeVerified, formSubmit]);

  useViewDidFocusScreenAnalyticsEvent({
    key: '/payments-detail/v2aReceiverPaymentDetails',
    customAttributes: customAttributes,
  });

  const insets = useSafeAreaInsets();

  return (
    <>
      <View
        style={[
          styles.container,
          {
            paddingBottom: insets.bottom,
            paddingTop: insets.top,
          },
        ]}
      >
        <KeyboardAwareScrollView
          keyboardShouldPersistTaps="handled"
          style={styles.scrollContainer}
          bottomOffset={100}
        >
          {challengeData && formData ? (
            <PaymentOTP
              formData={formData}
              challengeData={challengeData}
              setChallengeVerified={setChallengeVerified}
            />
          ) : (
            <>
              <View style={styles.headerContainer}>
                <ReceiverHeader />
                <ExpressiveSmText style={styles.headerText} numberOfLines={0}>
                  {translatedText('header', { amount: paymentDetails?.amount })}
                </ExpressiveSmText>
              </View>
              <View style={styles.formContainer}>
                <InlineAlert
                  bodyText={translatedText('inlineAlertText', {
                    amount: paymentDetails?.amount,
                  })}
                  alertType={InlineAlertType.Info}
                  iconKeyType="LOCK"
                />
                <Controller
                  control={control}
                  name="firstName"
                  render={({ field: { onChange, onBlur, ref, value } }) => {
                    return (
                      <VXLTextInput
                        ref={ref}
                        labelText={translatedText('firstName')}
                        errorText={errors.firstName?.message}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        value={value}
                        enterKeyHint="next"
                        onSubmitEditing={() => {
                          setFocus('lastName');
                        }}
                        autoFocus
                        autoComplete="given-name"
                        analyticsId="v2aFirstNameClicked"
                        customAttributes={customAttributes}
                        testID="text-input-firstName"
                      />
                    );
                  }}
                />
                <Controller
                  control={control}
                  name="lastName"
                  render={({ field: { onChange, onBlur, ref, value } }) => {
                    return (
                      <VXLTextInput
                        ref={ref}
                        labelText={translatedText('lastName')}
                        errorText={errors.lastName?.message}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        value={value}
                        analyticsId="v2aLastNameClicked"
                        customAttributes={customAttributes}
                        enterKeyHint="next"
                        onSubmitEditing={() => {
                          setFocus('email');
                        }}
                        autoComplete="family-name"
                        testID="text-input-lastName"
                      />
                    );
                  }}
                />
                <Controller
                  control={control}
                  name="email"
                  render={({ field: { onChange, onBlur, ref, value } }) => {
                    return (
                      <VXLTextInput
                        ref={ref}
                        labelText={translatedText('email')}
                        errorText={errors.email?.message}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        value={value}
                        autoCapitalize="none"
                        analyticsId="v2aEmailFieldClicked"
                        customAttributes={customAttributes}
                        enterKeyHint="next"
                        inputMode="email"
                        onSubmitEditing={() => {
                          setFocus('cardNumber');
                        }}
                        testID="text-input-email"
                      />
                    );
                  }}
                />
                <Controller
                  control={control}
                  name="cardNumber"
                  render={({ field: { onChange, onBlur, ref, value } }) => {
                    return (
                      <VXLTextInput
                        ref={ref}
                        labelText={translatedText('cardNumber')}
                        errorText={errors.cardNumber?.message}
                        onChangeText={(text) =>
                          onChange(formatCardNumber(text))
                        }
                        onBlur={onBlur}
                        value={value}
                        inputMode="numeric"
                        maxLength={19}
                        onSubmitEditing={() => {
                          setFocus('date');
                        }}
                        analyticsId="v2aDebitCardFieldClicked"
                        customAttributes={customAttributes}
                        testID="text-input-cardNumber"
                      />
                    );
                  }}
                />
                <View style={styles.formLine}>
                  <View style={styles.formLineItem}>
                    <Controller
                      control={control}
                      name="date"
                      render={({ field: { onChange, onBlur, ref, value } }) => {
                        return (
                          <VXLTextInput
                            ref={ref}
                            labelText={translatedText('expDate')}
                            errorText={errors.date?.message}
                            onChangeText={(text) =>
                              onChange(formatExpDate(text))
                            }
                            onBlur={onBlur}
                            value={value}
                            placeholder="MM/YY"
                            maxLength={5}
                            analyticsId="v2aCardExpirationDateFieldClicked"
                            customAttributes={customAttributes}
                            inputMode="numeric"
                            onSubmitEditing={() => {
                              setFocus('cvv');
                            }}
                            testID="text-input-expDate"
                          />
                        );
                      }}
                    />
                  </View>
                  <View style={styles.formLineItem}>
                    <Controller
                      control={control}
                      name="cvv"
                      render={({ field: { onChange, onBlur, ref, value } }) => {
                        return (
                          <VXLTextInput
                            ref={ref}
                            labelText={translatedText('cvv')}
                            errorText={errors.cvv?.message}
                            onChangeText={(text) =>
                              onChange(filterDigits(text))
                            }
                            onBlur={onBlur}
                            inputMode="numeric"
                            maxLength={3}
                            value={value}
                            onSubmitEditing={() => {
                              setFocus('state');
                            }}
                            analyticsId="v2aCVVFieldClicked"
                            customAttributes={customAttributes}
                            testID="text-input-cvv"
                          />
                        );
                      }}
                    />
                  </View>
                </View>
                <Controller
                  control={control}
                  name="state"
                  render={({ field: { onChange, onBlur, ref, value } }) => (
                    <VXLDropdown
                      ref={ref}
                      labelText={translatedText('state')}
                      placeholder={translatedText('state')}
                      options={stateList.map(getStateString)}
                      value={
                        value
                          ? (findFullStateString(stateList, value) ?? '')
                          : ''
                      }
                      onBlur={onBlur}
                      onChangeText={(text) => {
                        onChange(getStateAbbreviation(stateList, text));
                      }}
                      testID="state-select"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="zipCode"
                  render={({ field: { onChange, onBlur, ref, value } }) => {
                    return (
                      <VXLTextInput
                        ref={ref}
                        labelText={translatedText('zip')}
                        errorText={errors.zipCode?.message}
                        onChangeText={(text) => onChange(filterDigits(text))}
                        onBlur={onBlur}
                        keyboardType="numeric"
                        maxLength={5}
                        value={value}
                        autoCapitalize="none"
                        analyticsId="v2aZipFieldClicked"
                        customAttributes={customAttributes}
                        testID="text-input-zipCode"
                      />
                    );
                  }}
                />
                <Controller
                  control={control}
                  name="isActiveTerms"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <View>
                      <Checkbox
                        checked={Boolean(value)}
                        testId="agreeTerms"
                        onSelectedStateChange={() => {
                          onChange(!Boolean(value));
                          onBlur();
                        }}
                        analyticsId="v2aAgreementCheckboxClicked"
                        customAttributes={customAttributes}
                      >
                        <View style={styles.agreeTermsCheckbox}>
                          <BodyBaseText numberOfLines={0}>
                            {translatedText('agreeTerms1')}
                          </BodyBaseText>
                          <HyperlinkBaseText
                            numberOfLines={0}
                            onPress={handleTermsLinkPress}
                          >
                            {translatedText('agreeTermsLink')}
                          </HyperlinkBaseText>
                          <BodyBaseText numberOfLines={0}>
                            {translatedText('agreeTerms2')}
                          </BodyBaseText>
                          <HyperlinkBaseText
                            numberOfLines={0}
                            onPress={handlePolicyLinkPress}
                          >
                            {translatedText('agreePrivacyLink')}
                          </HyperlinkBaseText>
                          <BodyBaseText numberOfLines={0}>
                            {translatedText('agreeTerms3')}
                          </BodyBaseText>
                        </View>
                      </Checkbox>
                    </View>
                  )}
                />
                <Controller
                  control={control}
                  name="isActiveMarketing"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Checkbox
                      checked={Boolean(value)}
                      testId="agreeMarketing"
                      onSelectedStateChange={() => {
                        onChange(!Boolean(value));
                        onBlur();
                      }}
                      analyticsId="v2aMarketingCheckboxClicked"
                      customAttributes={customAttributes}
                    >
                      <BodyBaseText numberOfLines={0}>
                        {translatedText('agreeMarketing')}
                      </BodyBaseText>
                    </Checkbox>
                  )}
                />
                {errorResponse &&
                errorResponse.type === ErrorViewType.INLINE ? (
                  <View style={styles.errorWrapper}>
                    <View style={styles.icon}>
                      <Icon
                        iconKey="STATUS_ERROR"
                        iconColor={theme.color.status.negative}
                        iconSize={theme.spacing.m}
                      />
                    </View>
                    <BodyXSText style={styles.errorText} numberOfLines={0}>
                      {errorResponse.error}
                    </BodyXSText>
                  </View>
                ) : null}
                <Button
                  label={translatedText('button', {
                    amount: paymentDetails?.amount,
                  })}
                  isDisabled={!isValid}
                  onPress={handleSubmit(onSubmitAction)}
                  analyticsId="v2aReceiverSubmitted"
                  customAttributes={customAttributes}
                />
              </View>
            </>
          )}
        </KeyboardAwareScrollView>
        <ScreenTransitionOverlay
          isLoading={submitting}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={
            errorResponse && errorResponse.type === ErrorViewType.TERMINAL ? (
              <ErrorScreen
                messageHeadline={errorResponse.error}
                messageBody={errorResponse.subtitle}
                screenType={errorResponse.screenType || ErrorScreenType.Error}
                screenAnalyticsEventKey="v2aReceiverFlowError"
                customAttributes={customAttributes}
              />
            ) : null
          }
        />
      </View>
      {!isWeb ? (
        <KeyboardToolbar theme={keyboardToolbarTheme}>
          <KeyboardToolbar.Prev />
          <KeyboardToolbar.Next />
          <KeyboardToolbar.Done />
        </KeyboardToolbar>
      ) : null}
    </>
  );
};

const keyboardToolbarTheme: KeyboardToolbarProps['theme'] = {
  light: {
    background: theme.color.background.primary,
    primary: theme.color.interactive.primary,
    disabled: theme.color.interactive.disabled.text,
    ripple: theme.color.interactive.primary,
  },
  // dark is required - set to match light theme while we do not support dark mode
  dark: {
    background: theme.color.background.primary,
    primary: theme.color.interactive.primary,
    disabled: theme.color.interactive.disabled.text,
    ripple: theme.color.interactive.primary,
  },
};

const styles = StyleSheet.create({
  agreeTermsCheckbox: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.xs,
  },
  container: {
    backgroundColor: 'transparent',
    flex: 1,
  },
  formContainer: {
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  errorText: {
    color: theme.color.status.negative,
  },
  errorWrapper: {
    alignContent: 'center',
    alignItems: 'baseline',
    color: theme.color.status.negative,
    flexDirection: 'row',
    marginTop: theme.spacing.s,
  },
  formLine: {
    flexDirection: 'row',
    gap: theme.spacing.m,
  },
  formLineItem: {
    flex: 1,
  },
  headerContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.expressive.orchid,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.xl,
  },
  headerText: {
    textAlign: 'center',
  },
  icon: {
    alignSelf: 'center',
    paddingRight: theme.spacing.xs,
  },
  scrollContainer: {
    backgroundColor: theme.color.background.primary,
    ...(isWeb && {
      alignSelf: 'center',
      maxWidth: SMALL_SCREEN_MAX_WIDTH,
      width: '100%',
    }),
  },
});

export default PaymentsDetails;
